// Renders an assistant message as GitHub-flavoured markdown (spec-9): the model
// replies in prose with headings, lists, tables, code and emphasis, so the chat
// surface must format it rather than dump raw text. react-markdown + remark-gfm
// parse it; the components map styles each element with Backstage's design tokens
// (text-primary / text-muted / border-edge / bg-surface …) so it matches the rest
// of the app without pulling in the Tailwind typography plugin.
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

const components: Components = {
  p: ({ children }) => <p className="mb-3 last:mb-0 leading-relaxed">{children}</p>,
  h1: ({ children }) => (
    <h1 className="mb-2 mt-4 first:mt-0 text-lg font-semibold text-heading">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-2 mt-4 first:mt-0 text-base font-semibold text-heading">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-1 mt-3 first:mt-0 text-sm font-semibold text-heading">{children}</h3>
  ),
  ul: ({ children }) => (
    <ul className="mb-3 last:mb-0 list-disc space-y-1 pl-5">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-3 last:mb-0 list-decimal space-y-1 pl-5">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-primary">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-accent underline underline-offset-2 hover:opacity-80"
    >
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mb-3 border-l-2 border-edge pl-3 text-muted">{children}</blockquote>
  ),
  hr: () => <hr className="my-4 border-edge" />,
  code: ({ className, children }) => {
    // Inline code has no language class; fenced blocks are wrapped by <pre> below.
    const isBlock = typeof className === "string" && className.startsWith("language-");
    if (isBlock) {
      return <code className={`${className} text-[12px]`}>{children}</code>;
    }
    return (
      <code className="rounded bg-surface px-1 py-0.5 font-mono text-[12px] text-secondary">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="mb-3 last:mb-0 overflow-x-auto rounded-lg border border-edge bg-surface p-3 font-mono text-[12px] leading-relaxed text-secondary">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="mb-3 last:mb-0 overflow-x-auto">
      <table className="w-full border-collapse text-left text-[13px]">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="border-b border-edge">{children}</thead>,
  th: ({ children }) => (
    <th className="px-2 py-1 font-semibold text-secondary">{children}</th>
  ),
  td: ({ children }) => <td className="border-b border-edge px-2 py-1">{children}</td>,
};

export function Markdown({ children }: { children: string }) {
  return (
    <div data-testid="markdown" className="text-sm text-primary">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
